import {ThemeProvider} from "@zendeskgarden/react-theming";
import {ReactElement, useEffect, useState} from "react";
import {ViziblyTheme} from "../analyst/ViziblyZDGTheme";
import useAppStore from "../../hooks/useAppStore";
import * as css from "./styles/OpDriverModeling.module.scss";
import * as workflowCSS from "../../styles/workflows/workflowCSS.module.scss";
import Header from "./components/Header";
import {Grid} from "@zendeskgarden/react-grid";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {
    ForecastLocks,
    useGetForecastLocksLazyQuery,
    useGetSimplifiedRevenueLazyQuery
} from "../../__generated__/generated_types";
import {WorkflowStepNavProps} from "../../components/workflows/workflow-step-nav/WorkflowStepNav";
import {buildSimplifiedUnitTypes} from "./logic/HelperFunctions";

export type TSimplifiedUnitType = {
    id: string,
    type: string,
    startingMarketRent?: number,
    overrideMarketRent?: number,
    count: number,
    isCustom?: boolean,
}

export default function Renovations({onPreviousClick, onNextClick}: WorkflowStepNavProps): ReactElement {
    const appStore = useAppStore();
    const property = useProperties();

    const [triggerDownload, setTriggerDownload] = useState<boolean>(false);
    const [unitTypes, setUnitTypes] = useState<TSimplifiedUnitType[]>([]);
    const [simplifiedRevenueVars, setSimplifiedRevenueVars] = useState<{propertyId: string, budgetYear: number} | undefined>();
    const [forecastLocks, setForecastLocks] = useState<ForecastLocks>();
    const [getSimplifiedRevenue, {data, loading}] = useGetSimplifiedRevenueLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [getForecastLocks, {data: dataForecastLocks, loading: lockDataLoading}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });

    function fetchSimplifiedRevenue() {
        if (!simplifiedRevenueVars || loading) {
            return;
        }

        getSimplifiedRevenue({
            variables: simplifiedRevenueVars,
        });
    }

    useEffect(() => {
        appStore.set({isLoading: false});
        return () => undefined;
    }, []);

    useEffect(() => {
        if (!property || !property.currentProperty) {
            return;
        }

        setSimplifiedRevenueVars({
            propertyId: property.currentProperty.id,
            budgetYear: property.currentProperty.budgetYear,
        });

        getForecastLocks({
            variables: {
                propertyId: property.currentProperty.id,
                budgetYear: property.currentProperty.budgetYear,
            }
        });
    }, [property]);

    useEffect(() => {
        if (!simplifiedRevenueVars) {
            return;
        }

        fetchSimplifiedRevenue();
    }, [simplifiedRevenueVars]);

    useEffect(() => {
        if (!data || loading) {
            return;
        }

        const unitTypes = buildSimplifiedUnitTypes(data);

        setUnitTypes(unitTypes);
    }, [data, loading]);

    useEffect(() => {
        if (lockDataLoading || !dataForecastLocks?.forecastLocks) {
            setForecastLocks(undefined);
            return;
        }

        setForecastLocks(dataForecastLocks.forecastLocks.property);
    }, [dataForecastLocks, lockDataLoading]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${css.opDriverModelingWrapper} ${workflowCSS.workflowContainer}`}>
                <div className={workflowCSS.workflowContainerInner}>
                    <Header
                        title={"Renovations"}
                        prevAction={onPreviousClick}
                        nextAction={onNextClick}
                    />

                    <Grid className={css.bodyContent} gutters={"lg"}>
                       Renovations is no longer supported in Simplified Revenue.
                    </Grid>
                </div>
            </div>
        </ThemeProvider>
    );
}